import { TextInputProps } from "@components/TextInput";
import { AmountTextInput } from "@components/TextInput/AmountTextInput";

import {
  CoerceToSafeIntStatus,
  SafeInt,
  coerceToSafeInt,
} from "@every.org/common/src/codecs/number";
import { Currency } from "@every.org/common/src/entity/types";
import {
  DECIMAL_OFFSET_FOR_CURRENCY,
  minimumDenominationAmountToCurrencyValue,
} from "@every.org/common/src/helpers/currency";

type AmountInputProps = {
  amountValue: SafeInt | undefined;
  currency: Currency;
  setAmountValue: (amount: SafeInt | undefined) => void;
  validationErrors?: string;
} & Pick<
  TextInputProps,
  "id" | "name" | "labelText" | "data-tname" | "autoFocus" | "onBlur"
>;

export function AmountInput({
  amountValue,
  setAmountValue,
  currency,
  validationErrors,
  ...props
}: AmountInputProps) {
  const formatNumberWithCommas = (value: string) => {
    const [integer, decimal] = value.split(".");
    return Number(integer).toLocaleString() + (decimal ? "." + decimal : "");
  };

  const rawInputValue = amountValue
    ? minimumDenominationAmountToCurrencyValue({
        currency,
        amountInMinDenom: amountValue,
      }).amount.toString()
    : "";

  const inputValue = rawInputValue ? formatNumberWithCommas(rawInputValue) : "";

  const onAmountChange = (amount: string) => {
    if (amount === "") {
      setAmountValue(undefined);
      return;
    }

    try {
      const cleanedAmount = amount.replace(/,/g, "");

      const newValue = coerceToSafeInt({
        num:
          Number.parseInt(cleanedAmount) *
          Math.pow(10, DECIMAL_OFFSET_FOR_CURRENCY[currency]),
      });

      if (
        newValue.status === CoerceToSafeIntStatus.IS_SAFE_INT &&
        newValue.num >= 0
      ) {
        setAmountValue(newValue.num);
      }
    } catch (e) {
      return;
    }
  };

  return (
    <AmountTextInput
      amount={inputValue || ""}
      setAmount={onAmountChange}
      css={{ maxWidth: "unset" }}
      {...props}
      shorten
      amountErrorMessage={validationErrors}
      showCommas
      collapseDescriptionSpace
    />
  );
}
